/**
 * Environment configuration for Supabase.
 *
 * This module provides a simplified configuration that:
 * - Uses VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from environment variables
 * - Falls back to default local Supabase values for development
 * - Always calls OpenAI (no mock mode) - tests use vi.mock() instead
 */

// Production config (hardcoded for production domain)
const PRODUCTION_SUPABASE_URL = 'https://hbvjnndxtdarlyxsbyye.supabase.co';
const PRODUCTION_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhidmpubmR4dGRhcmx5eHNieXllIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg5MDI4MDcsImV4cCI6MjA4NDQ3ODgwN30.7P4v2TLoABUVBb5jlNFVndfLJ-a4NIHmH-3TnW6EzkM';

// Default local Supabase config (used as fallback in dev)
const DEFAULT_LOCAL_SUPABASE_URL = 'http://127.0.0.1:54321';
const DEFAULT_LOCAL_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

/**
 * Determines if the current hostname is localhost.
 */
export function isLocalhostHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Determines if the current hostname is a Lovable preview domain.
 * Preview domains end with .lovable.app and contain 'preview' in the subdomain.
 */
export function isLovablePreviewHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.endsWith('.lovable.app') && hostname.includes('preview');
}

/**
 * Determines if anonymous session logout should be available.
 * Available on localhost and Lovable preview environments.
 */
export function isDevEnvironment(): boolean {
  return isLocalhostHostname() || isLovablePreviewHostname();
}

/**
 * Gets the Supabase URL based on environment.
 * - On localhost: uses VITE_SUPABASE_URL or falls back to default local
 * - On production: uses hardcoded production URL
 */
export function getSupabaseUrl(): string {
  if (isLocalhostHostname()) {
    return import.meta.env.VITE_SUPABASE_URL || DEFAULT_LOCAL_SUPABASE_URL;
  }
  return PRODUCTION_SUPABASE_URL;
}

/**
 * Gets the Supabase anon key based on environment.
 * - On localhost: uses VITE_SUPABASE_ANON_KEY or falls back to default local
 * - On production: uses hardcoded production key
 */
export function getSupabaseAnonKey(): string {
  if (isLocalhostHostname()) {
    return import.meta.env.VITE_SUPABASE_ANON_KEY || DEFAULT_LOCAL_SUPABASE_ANON_KEY;
  }
  return PRODUCTION_SUPABASE_ANON_KEY;
}
